<template>
    <aside class="aside sidebar-wrapper" :class="{ 'drawer-open': isDrawerOpen }">
      <!-- START Sidebar (left)-->
      <div class="aside-inner">
      <!-- Close button for mobile -->
      <button
        class="drawer-close-btn"
        @click="closeDrawer"
        aria-label="Close menu"
      >
        <em class="fa fa-times"></em>
      </button>

      <nav data-sidebar-anyclick-close="" class="sidebar">
        <!-- START sidebar nav-->
        <ul class="nav">
          <!-- START user info-->
          <li class="has-user-block">
            <div id="user-block" class="collapse in">
              <div class="item user-block">
                <!-- User picture-->
                <div class="user-block-picture">
                  <div class="user-block-status">
                    <img v-if="activeAccount" :src="getAccountImgUrl(activeAccount.accountType)" alt="Avatar" width="60" height="60" class="img-thumbnail img-circle">
                  </div>
                </div>
                <!-- Name and Job-->
                <div class="user-block-info">
                  <span v-if="activeAccount" class="user-block-name" id="account-name">{{ activeAccount.accountName }}</span>
                  <span v-if="activeAccount" class="user-block-role">{{getAccountDescription(activeAccount.accountType)}}</span>
                  <span v-if="isAccountStatusAsPending() && !isFinancialAdvisor" class="status label label-warning">Pending</span>
                  <span v-if="isAccountStatusAsActive() && !isFinancialAdvisor" class="status label label-info">Active</span>
                </div>
              </div>
            </div>
          </li>
          <!-- END user info-->
          <!-- Iterates over all sidebar items-->
          <li v-if="isFinancialAdvisor" :class="{ active: $route.name === 'Home' }">
            <router-link :to="{ name: 'Home' }" title="Home">
              <em class="fa fa-home"></em>
              <span data-localize="sidebar.nav.WIDGETS">Home</span>
            </router-link>
          </li>
          <li v-if="activeAccount.accountType !== accountTypes.ADVISOR.name"
            :class="{ active: $route.name === 'AccountSummary', disabled: disableSubmit }">
            <router-link :to="{ name: 'AccountSummary' }" title="Account summary">
              <em class="icon-book-open"></em>
              <span data-localize="sidebar.nav.WIDGETS">Account summary</span>
            </router-link>
          </li>
          <li v-if="activeAccount.accountType !== accountTypes.ADVISOR.name && !isReadOnly"
            :class="{ active: $route.name === 'Notes', disabled: disableSubmit }">
            <router-link :to="{ name: 'Notes' }" title="Investments" replace>
              <em class="fa fa-money"></em>
              <span data-localize="sidebar.nav.WIDGETS">Investments</span>
            </router-link>
          </li>
          <li v-if="activeAccount.accountType !== accountTypes.ADVISOR.name && !isReadOnly"
            :class="{ active: $route.name === 'UpcomingTransactionsForInvestor', disabled: disableSubmit }">
            <router-link :to="{ name: 'UpcomingTransactionsForInvestor' }" title="Upcoming transactions" replace>
              <em class="fa fa-exchange"></em>
              <span data-localize="sidebar.nav.WIDGETS">Upcoming transactions</span>
            </router-link>
          </li>
          <li v-if="!isInstitutionAccount && activeAccount.accountType !== accountTypes.ADVISOR.name"
            :class="{ active: $route.name === 'AddFundingSource', disabled: disableSubmit }">
            <router-link :to="{ name: 'AddFundingSource' }" title="Bank accounts" :class="{'with-notification': !hasFundingSources}">
              <em class="fa fa-university"></em>
              <span data-localize="sidebar.nav.WIDGETS">Bank accounts</span>
              <notification-icon v-if="hasFundingSources === false && userProfile.accountType !== 'INSTITUTION'" :content="notificationMessage"></notification-icon>
            </router-link>
          </li>
          <li
            v-if="activeAccount.accountType !== accountTypes.ADVISOR.name"
            :class="{ active: isPreferencesViewActive, disabled: disableSubmit, 'accordion-list-background': preferencesAccordionOpen }"
          >
            <a v-on:click="togglePreferencesAccordion()">
              <em class="fa fa-globe"></em>
              <span data-localize="sidebar.nav.WIDGETS">Preferences</span>
            </a>
            <ul v-if="displayedPreferences.length > 0" class="nav accordion-submenu">
              <li
                v-for="(preference, index) in displayedPreferences"
                :key="`preference-${index}`"
                :class="{ active: $route.name === preference.route, disabled: disableSubmit }"
                class="accordion-list-background"
              >
                <router-link class="with-notification" :to="{ name: preference.route }" :title="preference.title">
                  <span
                    data-localize="sidebar.nav.WIDGETS"
                    class="accordion-list-item-text"
                  >{{ preference.title }}</span>
                  <notification-icon v-if="displayNotificationIcon(preference)" :content="notificationMessage"></notification-icon>
                </router-link>
              </li>
            </ul>
          </li>
          <li :class="{ active: $route.name === 'AccountDocuments', disabled: disableSubmit, 'accordion-list-background': documentsAccordionOpen }">
            <a v-on:click="toggleDocumentsAccordion()">
              <em class="fa fa-file-text-o"></em>
              <span data-localize="sidebar.nav.WIDGETS">Documents</span>
            </a>
            <ul v-if="documentOptions.length > 0" class="nav accordion-submenu">
              <li
                v-for="(document, index) in documentOptions"
                :key="`document-${index}`"
                :class="{ active: $route.name === document.value, disabled: disableSubmit }"
                class="accordion-list-background"
              >
                <router-link :to="{ name: 'AccountDocuments', query: { type: document.value } }" :title="document.text">
                  <span
                    data-localize="sidebar.nav.WIDGETS"
                    class="accordion-list-item-text"
                  >{{ document.text }}</span>
                </router-link>
              </li>
            </ul>
          </li>
          <li v-if="activeAccount.accountType !== accountTypes.ADVISOR.name"
            :class="{ active: $route.name === 'FeedbackSurvey', disabled: disableSubmit }">
            <router-link :to="{ name: 'FeedbackSurvey' }" title="Feedback"
              :class="{'with-notification': showFeedbackIndicator}">
              <em class="fa fa-comment-o"></em>
              <span data-localize="sidebar.nav.WIDGETS">Feedback</span>
              <notification-icon
                v-if="showFeedbackIndicator"
                :content="'We\'d love to hear your feedback.'"></notification-icon>
            </router-link>
          </li>
        </ul>
          <div class="col-lg-12" id="contact-message-container">
            <div class="alert alert-info alert-dismissible" role="alert" id="contact-message">
              <p>Contact us with any questions:</p>
              <a href="mailto:info@calvertimpact.org" target="_blank">info@calvertimpact.org</a>
            </div>
          </div>
        <!-- END sidebar nav-->
      </nav>
    </div>
    <!-- END Sidebar (left)-->
  </aside>
</template>

<script>
  import {
    mapActions,
    mapState,
    mapGetters,
  } from 'vuex';

  import {
    getAccounTypeProperties,
  } from '@/util';

  import FinancialAdvisorAvatar
    from '@/assets/img/user/img-profile-advisor.svg';

  import NotificationIcon from './widgets/NotificationIcon';

  export default {
    name: 'sideBar',
    props: {
      drawerOpen: {
        type: Boolean,
        default: false,
      },
    },
    async beforeMount() {
      if (!this.hasBeenCreatedAccount) {
        this.disableSubmit = true;
        return;
      }
      this.determineIfUserIsFinancialAdvisor();

      if (!this.isFinancialAdvisor) {
        await this.checkUserStatus();
      }
    },
    components: {
      'notification-icon': NotificationIcon,
    },
    data() {
      return {
        userProfile: {
          fullName: '',
          accountType: '',
        },
        financialAdvisorAvatar: FinancialAdvisorAvatar,
        disableSubmit: false,
        hasFundingSources: true,
        isFinancialAdvisor: false,
        documentsAccordionOpen: true,
        preferencesAccordionOpen: true,
        isDrawerOpen: false,
        overlayEl: null,
        preferences: [
          {
            route: 'InvestorPreferences',
            title: 'Impact',
          },
          {
            route: 'InvestorPaymentPreferences',
            title: 'Payment',
          },
          {
            route: 'InvestorCommunicationPreferences',
            title: 'Communication',
          },
        ],
      };
    },
    mounted() {
      this.createOverlay();
    },
    beforeDestroy() {
      this.removeOverlay();
    },
    watch: {
      drawerOpen(newVal) {
        this.isDrawerOpen = newVal;
        this.updateOverlay();
      },
      isDrawerOpen(newVal) {
        this.updateOverlay();
        if (newVal) {
          document.body.style.overflow = 'hidden';
        } else {
          document.body.style.overflow = '';
        }
      },
      $route() {
        if (this.isDrawerOpen) {
          this.closeDrawer();
        }
      },
    },
    methods: {
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('account', [
        'setNotificationStatus',
      ]),
      createOverlay() {
        if (typeof window === 'undefined') return;
        this.overlayEl = document.createElement('div');
        this.overlayEl.className = 'drawer-overlay';
        this.overlayEl.style.display = 'none';
        this.overlayEl.addEventListener('click', this.closeDrawer);
        document.body.appendChild(this.overlayEl);
      },
      removeOverlay() {
        if (this.overlayEl) {
          this.overlayEl.removeEventListener('click', this.closeDrawer);
          if (this.overlayEl.parentNode) {
            this.overlayEl.parentNode.removeChild(this.overlayEl);
          }
          this.overlayEl = null;
        }
      },
      updateOverlay() {
        if (this.overlayEl) {
          this.overlayEl.style.display = this.isDrawerOpen ? 'block' : 'none';
        }
      },
      openDrawer() {
        this.isDrawerOpen = true;
        document.body.style.overflow = 'hidden';
      },
      closeDrawer() {
        this.isDrawerOpen = false;
        document.body.style.overflow = '';
        this.$emit('drawer-closed');
      },
      toggleDrawer() {
        if (this.isDrawerOpen) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      },
      displayNotificationIcon(preference) {
        return preference.title === 'Communication' &&
        [
          'COMMUNICATION_PREFERENCE',
          'ENABLE_MFA',
        ].includes(this.notificationStatus);
      },
      isAccountStatusAsPending() {
        return this.accountStatus === 'pending' || this.accountStatus === 'denied';
      },
      isAccountStatusAsActive() {
        return this.accountStatus === 'active';
      },
      determineIfUserIsFinancialAdvisor() {
        const { userType } = this.user;
        this.isFinancialAdvisor = (userType === this.userTypes.FINANCIAL_ADVISOR.name);
      },
      getAccountDescription(type) {
        return getAccounTypeProperties(type).text;
      },
      getAccountImgUrl(type) {
        const accountTypeIcon = `${getAccounTypeProperties(type).icon}`;
        return require(`@/assets/img/user/${accountTypeIcon}.svg`); // eslint-disable-line
      },
      checkUserStatus() {
        const accountId = this.activeAccount.id;

        // Without a real account there is no status to fetch. Asking for
        // account 0 returns a 403, which the HttpService interceptor turns
        // into a redirect to Home.
        if (!accountId) {
          this.disableSubmit = false;
          return Promise.resolve();
        }

        return this.getUserStatus({
          accountId,
        }).then(({ hasFundingSources, notificationStatus }) => {
          if (!this.hasBeenCreatedAccount) {
            this.disableSubmit = true;
          }
          this.hasFundingSources = hasFundingSources;
          this.setNotificationStatus(notificationStatus);
        }).catch(() => {
          this.disableSubmit = false;
        });
      },
      togglePreferencesAccordion() {
        this.preferencesAccordionOpen = !this.preferencesAccordionOpen;
      },
      toggleDocumentsAccordion() {
        this.documentsAccordionOpen = !this.documentsAccordionOpen;
      },
    },
    computed: {
      ...mapState('app', [
        'user',
        'activeAccount',
        'accountTypes',
        'accountStatus',
        'userTypes',
      ]),
      ...mapState('account', [
        'notificationStatus',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
      ]),
      notificationMessage() {
        const messages = {
          COMMUNICATION_PREFERENCE: 'Link an account to securely receive faster payments and make new investments.',
          ENABLE_MFA: 'Enable multi-factor authentication for more security',
        };
        return messages[this.notificationStatus] || '';
      },
      isInstitutionAccount() {
        return this.activeAccount.accountType === this.accountTypes.INSTITUTION.name;
      },
      isPreferencesViewActive() {
        return (
          this.preferences.find(p => p.route === this.$route.name) !== undefined
        );
      },
      displayedPreferences() {
        return this.preferencesAccordionOpen &&
          this.activeAccount.accountType !== this.accountTypes.ADVISOR.name
          ? this.preferences
          : [];
      },
      isReadOnly() {
        return this.user && this.user.userType === this.userTypes.READ_ONLY.name;
      },
      showFeedbackIndicator() {
        // Highlight Feedback until the user has submitted it at least once.
        return !!this.user && !this.user.lastFeedbackAt;
      },
      documentOptions() {
        return this.documentsAccordionOpen ? [
          { text: 'Reports', value: 'reports' },
          { text: 'Monthly Statements', value: 'monthly' },
          { text: 'Tax Documents', value: 'tax' },
          { text: 'Transaction Notices', value: 'transaction' },
        ] : [];
      },
    },
  };
</script>

<!-- Global styles for overlay (not scoped because it's appended to body) -->
<style>
  .drawer-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1040;
    animation: fadeIn 0.3s ease-in-out;
    cursor: pointer;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (min-width: 1025px) {
    .drawer-overlay {
      display: none !important;
    }
  }
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .fa-home {
    font-size: 1.2em;
  }
  .status {
    display: inline !important;
  }
  .disabled {
    pointer-events: none;
  }

  .sidebar > .nav > li > a  {
    white-space: normal;
  }
  .sidebar > .nav > li > a em {
    position: absolute;
    top: 14px;
  }
  .sidebar > .nav > li > a span {
    margin-left: 30px;
    display: inline-block;
  }
  .wrapper > .aside {
    width: 240px;
  }
  .wrapper > .aside .aside-inner {
    width: 240px;
  }

  #contact-message-container {
    padding: 10px;
  }

  #contact-message {
    margin-right: 15px;
    text-align: center;
    padding: 10px 0;
    border-radius: 0;
  }

  #contact-message p {
    display: block;
    font-size: 13px;
    color: #686f71;
  }

  #contact-message a {
    font-weight: 800;
    color: #0c7f93;
    text-decoration: none;
    font-size: 13px;
  }

  #account-name {
    white-space: pre-wrap;
    padding: 0 2px;
    word-break: break-all;
  }

  .accordion-list-background {
    background-color: #fcfcfc;
    border-left-color: #23b7e5 !important;
  }

  .accordion-list-item-text {
    margin-left: 35px !important;
    user-select: none;
  }

  .accordion-submenu {
    padding-left: 0;
    margin: 0;
  }

  .with-notification {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  /* Container should not interfere with layout */
  .sidebar-container {
    position: relative;
    height: 100%;
  }

  /* Desktop styles - hide mobile-only elements */
  @media (min-width: 1025px) {
    .drawer-close-btn {
      display: none !important;
    }
  }

  /* Mobile drawer styles */
  @media (max-width: 1024px) {
    .wrapper > .aside.sidebar-wrapper {
      position: fixed !important;
      top: 0 !important;
      left: -240px !important;
      height: 100vh !important;
      width: 240px !important;
      z-index: 1050 !important;
      transition: left 0.3s ease-in-out !important;
      overflow-y: auto !important;
      background-color: #fff !important;
      margin-left: 0 !important;
      transform: none !important;
      -webkit-transform: none !important;
    }

    .wrapper > .aside.sidebar-wrapper.drawer-open {
      left: 0 !important;
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.15);
      transform: none !important;
      -webkit-transform: none !important;
    }

    .wrapper > .aside.sidebar-wrapper .aside-inner {
      padding-top: 50px !important;
      width: 240px !important;
      position: static !important;
    }

    .drawer-close-btn {
      display: block;
      position: absolute;
      top: 10px;
      right: 10px;
      background: transparent;
      border: none;
      font-size: 24px;
      color: #666;
      cursor: pointer;
      z-index: 1051;
      padding: 8px 12px;
      line-height: 1;
    }

    .drawer-close-btn:hover {
      color: #000;
    }
  }

</style>



// WEBPACK FOOTER //
// SideBar.vue?de0ebce8